// Text rules shared by the validator and the renderer (pure, server-safe).
//
// The register rules are reconstructed from Leah's corrections in the build
// conversations (her note, §7) rather than from a specification; the missing
// `references/register-pass.md` is an open item in the plan. Every check here
// is a heuristic that WARNS. Nothing in this file blocks.

const TAG_RE = /<[^>]+>/g;
const ENTITY_RE = /&(?:nbsp|middot|hellip|ldquo|rdquo|lsquo|rsquo|amp|lt|gt|quot|#\d+);/g;

/** Entity and numeric forms of the en and em dash. */
const DASH_ENTITY_RE = /&(?:ndash|mdash);|&#(?:8211|8212);|&#x(?:2013|2014);/gi;

export function stripHtml(html: string | undefined | null): string {
  if (!html) return '';
  return html
    .replace(DASH_ENTITY_RE, '-')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(TAG_RE, '')
    .replace(ENTITY_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function wordCount(text: string): number {
  const t = stripHtml(text);
  if (!t) return 0;
  return t.split(/\s+/).filter(Boolean).length;
}

/** Split prose into sentences. Good enough for a length gate; not a parser. */
export function sentences(text: string): string[] {
  const t = stripHtml(text);
  if (!t) return [];
  return t
    .split(/(?<=[.!?])\s+(?=[A-Z0-9"'(])/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function longSentences(text: string, max = 24): string[] {
  return sentences(text).filter((s) => s.split(/\s+/).length > max);
}

/** Em dash, en dash (literal or entity-encoded), or the double-hyphen people type instead. */
export function hasDash(text: string): boolean {
  return /[–—]|(?<!-)--(?!-)/.test(text) || /&(?:ndash|mdash);|&#(?:8211|8212);|&#x(?:2013|2014);/i.test(text);
}

/** `{optN}` references in a feedback string → the N values. */
export function optRefs(text: string): number[] {
  const out: number[] = [];
  const re = /\{opt(\d+)\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) out.push(parseInt(m[1], 10));
  return out;
}

/** Leah's banned register: never, in any context. */
export const BANNED_REGISTER: readonly string[] = [
  'delve',
  'tapestry',
  'multifaceted',
  'underscores',
  'a testament to',
  'plays a crucial role',
  'it is important to note',
  'it is worth noting',
  "in today's world",
  'navigating the complexities',
  'rich history',
  'when it comes to',
  'the world of',
  'serves as',
  'stands as',
  'at its core',
  'ultimately',
];

/** Idioms an ESL reader takes literally. */
export const BANNED_IDIOMS: readonly string[] = [
  'walk away',
  'ended up',
  'came down to',
  'boils down',
  'at the end of the day',
  'off the table',
  'on the table',
  'goes to show',
  'in a nutshell',
  'hold water',
  'a far cry',
  'hand in hand',
  'falls apart',
  'goes without saying',
];

export function findBanned(text: string, list: readonly string[] = [...BANNED_REGISTER, ...BANNED_IDIOMS]): string[] {
  const t = stripHtml(text).toLowerCase();
  return list.filter((phrase) => t.includes(phrase));
}

/** "you", "your", "yours" anywhere in the prose. */
export function hasSecondPerson(text: string): boolean {
  return /\byou(r|rs)?\b/i.test(stripHtml(text));
}

/**
 * Does the feedback name what the error costs? A vocabulary heuristic: mark
 * words, credit words, or an exam-consequence verb. Over-fires less than it
 * under-fires; read each flag inside its block.
 */
export function hasConsequence(text: string): boolean {
  return /\b(mark|marks|marker|examiner|credit|award|awarded|earn|earns|earning|cost|costs|lose|loses|losing|nothing|zero|full marks|no marks|penal)/i.test(
    stripHtml(text),
  );
}

/**
 * Words that give away the expected level or quality of a sample answer.
 * Both authors' notes carry this rule; the validator blocks on it.
 */
export function levelReveal(text: string): string | null {
  const t = stripHtml(text);
  const m = t.match(/\b(Level\s*[1-7]\b|Expected(?:\s+mark)?|weak answer|strong answer|model answer|good answer|bad answer)/i);
  return m ? m[0] : null;
}

const STOP = new Set(
  'the a an and or of to in on at by for with from as is are was were be been that this these those it its their his her they them he she we you your our not no but which who whom what when where how why than then there here into over under about after before between during through up down out off all any each more most some such only own same so very can will would should could may might must do does did have has had having'.split(
    ' ',
  ),
);

/** Content words (lowercased, ≥ 4 letters, not a stop word). */
export function contentWords(text: string): Set<string> {
  const out = new Set<string>();
  for (const w of stripHtml(text).toLowerCase().split(/[^a-z0-9']+/)) {
    if (w.length >= 4 && !STOP.has(w)) out.add(w);
  }
  return out;
}

/** Share of `a`'s content words that also appear in `b`. 0 when `a` is empty. */
export function overlapRatio(a: string, b: string): number {
  const wa = contentWords(a);
  if (wa.size === 0) return 0;
  const wb = contentWords(b);
  let hit = 0;
  for (const w of wa) if (wb.has(w)) hit++;
  return hit / wa.size;
}
